import React, { useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Grid,
  Chip,
} from '@mui/material';
import { useAnalysisContext } from '../../context/AnalysisContext';
import { ErrorDisplay } from '../common/ErrorDisplay';
import AdvancedOptionsDialog from './AdvancedOptionsDialog';

interface TaskSelectorProps {
  hasStyleGuide?: boolean;
}

export const TaskSelector: React.FC<TaskSelectorProps> = ({ hasStyleGuide = false }) => {
  const {
    tasks,
    selectedTasks,
    loading,
    error,
    loadTasks,
    toggleTask,
    selectAllTasks,
    clearTasks,
  } = useAnalysisContext();
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  useEffect(() => {
    if (tasks.length === 0) {
      loadTasks();
    }
  }, [tasks.length, loadTasks]);

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Loading available tasks...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <ErrorDisplay error={error} onRetry={loadTasks} />
      </Paper>
    );
  }

  // Standard tasks that the user can freely toggle
  const standardTasks = tasks.filter(t => !t.requiresStyleGuide);
  // Style-guide tasks are shown only when a style guide is uploaded
  const styleGuideTasks = hasStyleGuide ? tasks.filter(t => t.requiresStyleGuide) : [];

  const allSelected = standardTasks.every(t => selectedTasks.includes(t.taskId));
  const someSelected = standardTasks.some(t => selectedTasks.includes(t.taskId)) && !allSelected;

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Select Analysis Tasks
        </Typography>
        <Box>
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAdvancedOpen(true);
            }}
            sx={{ mr: 2 }}
          >
            Advanced Options
          </Button>
          <Button
            type="button"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              selectAllTasks();
            }}
            disabled={allSelected}
            sx={{ mr: 1 }}
          >
            Select All
          </Button>
          <Button
            type="button"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearTasks();
            }}
            disabled={selectedTasks.length === 0}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => {
                e.stopPropagation();
                if (allSelected) {
                  clearTasks();
                } else {
                  selectAllTasks();
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          }
          label={
            <Typography fontWeight={600}>
              All Tasks ({selectedTasks.filter(id => standardTasks.some(t => t.taskId === id)).length}/{standardTasks.length} selected)
            </Typography>
          }
          onClick={(e) => e.stopPropagation()}
        />
        
        <Box 
          pl={4} 
          mt={2}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            bgcolor: 'background.default'
          }}
        >
          <Grid container spacing={2}>
            {standardTasks.map((task) => (
              <Grid item xs={12} sm={6} key={task.taskId}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTasks.includes(task.taskId)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleTask(task.taskId);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {task.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.description}
                      </Typography>
                    </Box>
                  }
                  onClick={(e) => e.stopPropagation()}
                  sx={{ 
                    mb: 0, 
                    alignItems: 'flex-start',
                    width: '100%',
                    ml: 0
                  }}
                />
              </Grid>
            ))}
            {styleGuideTasks.map((task) => (
              <Grid item xs={12} sm={6} key={task.taskId}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTasks.includes(task.taskId)}
                      disabled
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label={
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" fontWeight={500}>
                          {task.name}
                        </Typography>
                        <Chip label="Auto-included" size="small" color="info" variant="outlined" />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {task.description}
                      </Typography>
                    </Box>
                  }
                  onClick={(e) => e.stopPropagation()}
                  sx={{ 
                    mb: 0, 
                    alignItems: 'flex-start',
                    width: '100%',
                    ml: 0
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </FormGroup>

      {selectedTasks.length === 0 && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          Please select at least one analysis task to continue.
        </Typography>
      )}

      <AdvancedOptionsDialog open={advancedOpen} onClose={() => setAdvancedOpen(false)} />
    </Paper>
  );
};

